import { useEffect, useMemo, useRef, useState } from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useFilters,
} from "react-table";

import FilterComponent from "../../screens/User/Users/FilterComponent";
import { GlobalFilter } from "../../screens/User/Users/GlobalFilter";
import CustomLoader from "../CustomLoader";
import StyledPagination from "../StyledPagination/PaginationComponent";
import { Caption } from "../Table/style";
import "./styledTable.css";
import {
  SortDownIcon,
  SortIcon,
  SortUpIcon,
  renderToolTipContent,
} from "./Utils";
import {
  SearchGridItem,
  AddIncidentGridItem,
  Tr,
  TitleBar,
  Tag,
} from "./StyledTable.styles";
import {
  AddIcon,
  AddIncidentButton,
  AddIncidentButtonContainer,
} from "../../screens/User/Users/User.style";
import addIcon from "../../assets/images/add_icon.svg";
import { isArray } from "lodash";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import FilterComponentGeneral from "../../screens/User/Users/FilterComponentGeneral";
import { ButtonSpan } from "../../screens/User/dashboard/home/home.style";
import { useFilterWrapper, useTableFiltersWrapper } from "../../store/store";
import { handleCSVDownload, handleTrendPDFDownload } from "../../utils/helpers/pdfCsvUtils";
import moment from "moment";

interface TableInstance extends ReturnType<typeof useTable> {
  page: any;
  nextPage: () => void;
  previousPage: () => void;
  canNextPage: boolean;
  canPreviousPage: boolean;
  pageOptions: any;
  state: any;
  setPageSize: (arg0: number) => void;
  prepareRow: (arg0: any) => void;
  gotoPage: (arg0: number) => void;
  setGlobalFilter: (arg0: string) => void;
  pageCount: number;
  selectedFlatRows: any;
  getToggleAllRowsSelectedProps: any;
  getSorByToggleProps: any;
  updateData: (obj?: any) => void;
  doAddIncident?: any;
  setFilter: any;
  showAddNewUserBtn?: boolean;
  draft?: boolean;
  noPagination?: boolean;
  demarkated?: boolean;
}

/**
 * Export-related props for StyledTable component.
 * These props enable CSV and PDF export functionality for trend reports.
 */
interface ExportProps {
  /** Whether to show the Excel/PDF export buttons. Defaults to false. */
  showExportBtn?: boolean;
  /** Base filename for exported files (without extension). Required if showExportBtn is true. */
  exportFileName?: string;
  /** Start date for the report period. Formatted as "MM/DD/YYYY" in exports. Required if showExportBtn is true. */
  exportStartDate?: Date | string;
  /** End date for the report period. Formatted as "MM/DD/YYYY" in exports. Required if showExportBtn is true. */
  exportEndDate?: Date | string;
  /** DOM element ID of the chart to include in PDF export (e.g., "line-chart"). Optional. */
  exportChartId?: string;
  /** Facility name to display in export headers. Optional. */
  exportFacilityName?: string;
}

/**
 * Props for the StyledTable component.
 * Note: Most props use 'any' type for backward compatibility with existing usage.
 */
interface StyledTableProps extends ExportProps {
  // Core table props
  tableCols?: any[];
  tableData?: any;
  caption?: any;

  // Filter and search props
  showFilterComponent?: any;
  showSearchBar?: boolean;
  isLoading?: any;
  getUserCb?: any;
  half?: any;
  rolesData?: any;
  facilitiesData?: any;
  incidentStatusData?: any;
  incidentTypeData?: any;
  showSelectFacilitiesComponent?: any;

  // Action props
  showAddIncidentButton?: any;
  updateData?: any;
  doAddIncident?: any;

  // Styling props
  styledRow?: any;
  showTitleBar?: any;
  barTitle?: any;
  hasTag?: any;
  tagPosition?: any;

  // Pagination props
  handleNextPage?: any;
  handlePreviousPage?: any;
  currentPage?: any;
  isFetching?: any;
  serverPaginated?: any;
  noOfPages?: any;
  goToFirstPage?: any;
  goToLastPage?: any;
  handlePageSize?: any;

  // User management props
  showAddNewUserBtn?: any;
  setSearchQuery?: any;
  searchable?: any;
  incidentTypeParam?: any;
  facilityNameParam?: any;
  searchPlaceholder?: any;
  draft?: any;
  onUserList?: any;
  whereUsed?: any;

  // Table behavior props
  noPagination?: any;
  demarkated?: any;
}

const StyledTable = ({
  tableCols = [],
  tableData,
  caption,
  showFilterComponent,
  showSearchBar = false,
  isLoading,
  getUserCb,
  half,
  rolesData = [],
  facilitiesData = [],
  incidentStatusData,
  incidentTypeData,
  showSelectFacilitiesComponent,
  showAddIncidentButton,
  updateData,
  doAddIncident,
  styledRow,
  showTitleBar,
  barTitle,
  hasTag,
  tagPosition,
  handleNextPage,
  handlePreviousPage,
  currentPage,
  isFetching,
  serverPaginated,
  noOfPages,
  goToFirstPage,
  goToLastPage,
  handlePageSize,
  showAddNewUserBtn,
  setSearchQuery,
  searchable,
  incidentTypeParam,
  facilityNameParam,
  searchPlaceholder,
  draft,
  onUserList,
  whereUsed,
  noPagination,
  demarkated,
  // Export props - see ExportProps interface for detailed documentation
  showExportBtn = false,
  exportFileName,
  exportStartDate,
  exportEndDate,
  exportChartId,
  exportFacilityName,
}: StyledTableProps) => {
  const tableContainer = useRef<HTMLDivElement>(null);
  const columns = useMemo(() => tableCols, [tableCols]);
  const data = useMemo(
    () => (isArray(tableData) ? tableData : []),
    [tableData]
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      // @ts-ignore
      updateData,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => {
        return columns;
      });
    }
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    state,
    gotoPage,
    pageCount,
    setGlobalFilter,
  } = tableInstance as TableInstance;

  const {
    roleFilter,
    setRoleFilter,
    userFacilityFilter,
    setUserFacilityFilter,
  } = useFilterWrapper((state: any) => ({
    roleFilter: state.roleFilter,
    setRoleFilter: state.setRoleFilter,
    userFacilityFilter: state.userFacilityFilter,
    setUserFacilityFilter: state.setUserFacilityFilter,
  }));

  const {
    incidentTypeFilter,
    setIncidentTypeFilter,
    incidentStatusFilter,
    setIncidentStatusFilter,
    incidentFacilityFilter,
    setIncidentFacilityFilter,
    incidentStartDateFilter,
    setIncidentStartDateFilter,
    incidentEndDateFilter,
    setIncidentEndDateFilter,
    draftIncidentTypeFilter,
    setDraftIncidentTypeFilter,
    draftFacilityFilter,
    setDraftFacilityFilter,
    draftStartDateFilter,
    setDraftStartDateFilter,
    draftEndDateFilter,
    setDraftEndDateFilter,
    deletedIncidentTypeFilter,
    setDeletedIncidentTypeFilter,
    deletedIncidentStatusFilter,
    setDeletedIncidentStatusFilter,
    deletedIncidentFacilityFilter,
    setDeletedIncidentFacilityFilter,
    deletedStartDateFilter,
    setDeletedStartDateFilter,
    deletedEndDateFilter,
    setDeletedEndDateFilter,
  } = useTableFiltersWrapper((state) => ({
    incidentTypeFilter: state.incidentTypeFilter,
    setIncidentTypeFilter: state.setIncidentTypeFilter,
    incidentStatusFilter: state.incidentStatusFilter,
    setIncidentStatusFilter: state.setIncidentStatusFilter,
    incidentFacilityFilter: state.incidentFacilityFilter,
    setIncidentFacilityFilter: state.setIncidentFacilityFilter,
    incidentStartDateFilter: state.incidentStartDateFilter,
    setIncidentStartDateFilter: state.setIncidentStartDateFilter,
    incidentEndDateFilter: state.incidentEndDateFilter,
    setIncidentEndDateFilter: state.setIncidentEndDateFilter,
    draftIncidentTypeFilter: state.draftIncidentTypeFilter,
    setDraftIncidentTypeFilter: state.setDraftIncidentTypeFilter,
    draftFacilityFilter: state.draftFacilityFilter,
    setDraftFacilityFilter: state.setDraftFacilityFilter,
    draftStartDateFilter: state.draftStartDateFilter,
    setDraftStartDateFilter: state.setDraftStartDateFilter,
    draftEndDateFilter: state.draftEndDateFilter,
    setDraftEndDateFilter: state.setDraftEndDateFilter,
    deletedIncidentTypeFilter: state.deletedIncidentTypeFilter,
    setDeletedIncidentTypeFilter: state.setDeletedIncidentTypeFilter,
    deletedIncidentStatusFilter: state.deletedIncidentStatusFilter,
    setDeletedIncidentStatusFilter: state.setDeletedIncidentStatusFilter,
    deletedIncidentFacilityFilter: state.deletedIncidentFacilityFilter,
    setDeletedIncidentFacilityFilter: state.setDeletedIncidentFacilityFilter,
    deletedStartDateFilter: state.deletedStartDateFilter,
    setDeletedStartDateFilter: state.setDeletedStartDateFilter,
    deletedEndDateFilter: state.deletedEndDateFilter,
    setDeletedEndDateFilter: state.setDeletedEndDateFilter,
  }));

  const { globalFilter = "", pageIndex, pageSize } = state;
  const [clearReactSelect, setClearReactSelect] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const applyFilter = (items: any, key: any, value: any) => {
    return items.filter((item: any) => {
      const field = item.original[key];
      return field?.toLowerCase().includes(value.toLowerCase());
    });
  };

  const shouldApplyFilter = (
    filter: string,
    setFilter: (param: string) => void,
    context: string
  ) => filter && !setFilter && whereUsed === context;

  const filteredData = useMemo(() => {
    let filtered = page;

    const filterConditions = [
      {
        shouldFilter: shouldApplyFilter(roleFilter, setRoleFilter, "userList"),
        key: "userRoleName",
        value: roleFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          userFacilityFilter,
          setUserFacilityFilter,
          "userList"
        ),
        key: "Facilities",
        value: userFacilityFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          incidentFacilityFilter,
          setIncidentFacilityFilter,
          "incidentList"
        ),
        key: "Facilities",
        value: incidentFacilityFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          draftFacilityFilter,
          setDraftFacilityFilter,
          "draftIncidentList"
        ),
        key: "Facilities",
        value: draftFacilityFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          draftIncidentTypeFilter,
          setDraftIncidentTypeFilter,
          "draftIncidentList"
        ),
        key: "type",
        value: draftIncidentTypeFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          draftStartDateFilter,
          setDraftStartDateFilter,
          "draftIncidentList"
        ),
        key: "startDate",
        value: draftStartDateFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          draftEndDateFilter,
          setDraftEndDateFilter,
          "draftIncidentList"
        ),
        key: "endDate",
        value: draftEndDateFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          incidentTypeFilter,
          setIncidentTypeFilter,
          "incidentList"
        ),
        key: "type",
        value: incidentTypeFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          incidentStatusFilter,
          setIncidentStatusFilter,
          "incidentList"
        ),
        key: "Incident_Status__c",
        value: incidentStatusFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          incidentStartDateFilter,
          setIncidentStartDateFilter,
          "incidentList"
        ),
        key: "startDate",
        value: incidentStartDateFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          incidentEndDateFilter,
          setIncidentEndDateFilter,
          "incidentList"
        ),
        key: "endDate",
        value: incidentEndDateFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          deletedIncidentFacilityFilter,
          setDeletedIncidentFacilityFilter,
          "deletedList"
        ),
        key: "Facilities",
        value: deletedIncidentFacilityFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          deletedIncidentTypeFilter,
          setDeletedIncidentTypeFilter,
          "deletedList"
        ),
        key: "type",
        value: deletedIncidentTypeFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          deletedIncidentStatusFilter,
          setDeletedIncidentStatusFilter,
          "deletedList"
        ),
        key: "Incident_Status__c",
        value: deletedIncidentStatusFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          deletedStartDateFilter,
          setDeletedStartDateFilter,
          "deletedList"
        ),
        key: "startDate",
        value: deletedStartDateFilter,
      },
      {
        shouldFilter: shouldApplyFilter(
          deletedEndDateFilter,
          setDeletedEndDateFilter,
          "deletedList"
        ),
        key: "endDate",
        value: deletedEndDateFilter,
      },
    ];

    filterConditions.forEach(({ shouldFilter, key, value }) => {
      if (shouldFilter) {
        filtered = applyFilter(filtered, key, value);
      }
    });

    return filtered;
  }, [
    page,
    roleFilter,
    userFacilityFilter,
    incidentTypeFilter,
    incidentStatusFilter,
    incidentFacilityFilter,
    draftFacilityFilter,
    draftIncidentTypeFilter,
    draftStartDateFilter,
    draftEndDateFilter,
    incidentStartDateFilter,
    incidentEndDateFilter,
    deletedIncidentFacilityFilter,
    deletedIncidentTypeFilter,
    deletedIncidentStatusFilter,
    deletedStartDateFilter,
    deletedEndDateFilter,
    whereUsed,
  ]);

  function handleSortIconDisplay(column: any) {
    if (column.isSorted) {
      return column.isSortedDesc ? (
        <SortDownIcon className="h-4 w-4 text-gray-400" />
      ) : (
        <SortUpIcon className="h-4 w-4 text-gray-400" />
      );
    }
    return (
      <SortIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100" />
    );
  }

  const incidentStatus = incidentStatusData ? [...incidentStatusData] : [];
  const incidentType = incidentTypeData ? [...incidentTypeData] : [];

  const textIsLong = (text: any) =>
    text?.constructor === String && text.split(" ").length >= 3;

  const handleClearFilter = () => {
    const clearFilterActions = [
      setSearchQuery,
      setUserFacilityFilter,
      !facilityNameParam && setIncidentFacilityFilter,
      setDraftFacilityFilter,
      setRoleFilter,
      !incidentTypeParam && setIncidentTypeFilter,
      setDraftIncidentTypeFilter,
      setDraftStartDateFilter,
      setDraftEndDateFilter,
      setIncidentStatusFilter,
      setIncidentStartDateFilter,
      setIncidentEndDateFilter,
      setDeletedIncidentFacilityFilter,
      setDeletedIncidentTypeFilter,
      setDeletedIncidentStatusFilter,
      setDeletedStartDateFilter,
      setDeletedEndDateFilter,
    ].filter(Boolean); // This filters out any false values

    clearFilterActions.forEach((clearAction) => {
      if (typeof clearAction === "function") {
        clearAction("");
      }
    });

    const clearLocalFilters = [
      setIncidentTypeFilter,
      setDraftIncidentTypeFilter,
      setIncidentStatusFilter,
      setIncidentFacilityFilter,
      setDraftFacilityFilter,
      setDraftStartDateFilter,
      setDraftEndDateFilter,
      setUserFacilityFilter,
      setRoleFilter,
      setIncidentStartDateFilter,
      setIncidentEndDateFilter,
      deletedIncidentFacilityFilter,
      setDeletedIncidentTypeFilter,
      setDeletedIncidentStatusFilter,
      setDeletedStartDateFilter,
      setDeletedEndDateFilter,
    ];

    clearLocalFilters.forEach((clearFilter) => {
      if (typeof clearFilter === "function") {
        clearFilter("");
      }
    });

    setSearchValue("");
    setGlobalFilter("");
    setClearReactSelect((prev) => !prev);
  };

  const prevFilters: any = useRef({
    searchValue: "",
    roleFilter: "",
    userFacilityFilter: "",
    incidentTypeFilter: "",
    incidentStatusFilter: "",
    incidentFacilityFilter: "",
    draftIncidentFacilityFilter: "",
    draftIncidentTypeFilter: "",
    draftStartDateFilter: "",
    draftEndDateFilter: "",
    incidentStartDateFilter: "",
    incidentEndDateFilter: "",
    deletedIncidentFacilityFilter: "",
    deletedTypeFilter: "",
    deletedIncidentStatusFilter: "",
    deletedStartDateFilter: "",
    deletedEndDateFilter: "",
  });

  useEffect(() => {
    const queries = [
      { query: setSearchQuery, value: searchValue, key: "searchValue" },
      { query: setRoleFilter, value: roleFilter, key: "roleFilter" },
      {
        query: setUserFacilityFilter,
        value: userFacilityFilter,
        key: "userFacilityFilter",
      },
      {
        query: setIncidentFacilityFilter,
        value: incidentFacilityFilter,
        key: "incidentFacilityFilter",
      },
      {
        query: setIncidentTypeFilter,
        value: incidentTypeFilter,
        key: "incidentTypeFilter",
      },
      {
        query: setDraftIncidentTypeFilter,
        value: draftIncidentTypeFilter,
        key: "draftIncidentTypeFilter",
      },
      {
        query: setDraftStartDateFilter,
        value: draftStartDateFilter,
        key: "draftStartDateFilter",
      },
      {
        query: setDraftEndDateFilter,
        value: draftEndDateFilter,
        key: "draftEndDateFilter",
      },
      {
        query: setIncidentStatusFilter,
        value: incidentStatusFilter,
        key: "incidentStatusFilter",
      },
      {
        query: setDraftFacilityFilter,
        value: draftFacilityFilter,
        key: "draftFacilityFilter",
      },
      {
        query: setIncidentStartDateFilter,
        value: incidentStartDateFilter,
        key: "incidentStartDateFilter",
      },
      {
        query: setIncidentEndDateFilter,
        value: incidentEndDateFilter,
        key: "incidentEndDateFilter",
      },
      {
        query: setDeletedIncidentFacilityFilter,
        value: deletedIncidentFacilityFilter,
        key: "deletedIncidentFacilityFilter",
      },
      {
        query: setDeletedIncidentTypeFilter,
        value: deletedIncidentTypeFilter,
        key: "deletedIncidentTypeFilter",
      },
      {
        query: setDeletedIncidentStatusFilter,
        value: deletedIncidentStatusFilter,
        key: "deletedIncidentStatusFilter",
      },
      {
        query: setDeletedStartDateFilter,
        value: deletedStartDateFilter,
        key: "deletedStartDateFilter",
      },
      {
        query: setDeletedEndDateFilter,
        value: deletedEndDateFilter,
        key: "deletedEndDateFilter",
      },
    ];

    queries.forEach(({ query, value, key }) => {
      if (prevFilters.current[key] !== value) {
        if (query) query(value);
        prevFilters.current[key] = value;
      }
    });
  }, [
    searchValue,
    roleFilter,
    userFacilityFilter,
    incidentTypeFilter,
    incidentStatusFilter,
    incidentFacilityFilter,
    draftFacilityFilter,
    draftIncidentTypeFilter,
    draftStartDateFilter,
    draftEndDateFilter,
    incidentStartDateFilter,
    incidentEndDateFilter,
    deletedIncidentFacilityFilter,
    deletedIncidentTypeFilter,
    deletedIncidentStatusFilter,
    deletedStartDateFilter,
    deletedEndDateFilter,
  ]);

  const getStartDate = () => {
    if (incidentStartDateFilter?.length) {
      return incidentStartDateFilter;
    }
    if (draftStartDateFilter.length) {
      return draftStartDateFilter;
    }
    if (deletedStartDateFilter.length) {
      return deletedStartDateFilter;
    }
    return "";
  };

  const getEndDate = () => {
    if (incidentEndDateFilter?.length) {
      return incidentEndDateFilter;
    }
    if (draftEndDateFilter.length) {
      return draftEndDateFilter;
    }
    if (deletedEndDateFilter.length) {
      return deletedEndDateFilter;
    }
    return "";
  };

  const getSetStartDate = () => {
    if (whereUsed === "incidentList") {
      return setIncidentStartDateFilter;
    }
    if (whereUsed === "draftIncidentList") {
      return setDraftStartDateFilter;
    }
    if (whereUsed === "deletedList") {
      return setDeletedStartDateFilter;
    }
    return setIncidentStartDateFilter;
  };

  const getSetEndDate = () => {
    if (whereUsed === "incidentList") {
      return setIncidentEndDateFilter;
    }
    if (whereUsed === "draftIncidentList") {
      return setDraftEndDateFilter;
    }
    if (whereUsed === "deletedList") {
      return setDeletedEndDateFilter;
    }
    return setIncidentEndDateFilter;
  };

  // Export handlers for trend reports
  const handleExcelExport = () => {
    if (!showExportBtn) return;
    
    const fileName = exportFileName || `Export ${moment().format("MM-DD-YYYY")}`;
    handleCSVDownload(
      data,
      columns,
      fileName,
      exportFacilityName,
      exportStartDate,
      exportEndDate
    );
  };

  const handlePDFExport = async () => {
    if (!showExportBtn) return;
    
    const fileName = exportFileName || `Export ${moment().format("MM-DD-YYYY")}`;
    try {
      await handleTrendPDFDownload({
        data,
        columnData: columns,
        fileName,
        startDate: exportStartDate || new Date(),
        endDate: exportEndDate || new Date(),
        chartID: exportChartId,
        facilityName: exportFacilityName,
      });
    } catch (error) {
      console.error("Failed to generate PDF export:", error);
    }
  };

  return (
    <>
      {showFilterComponent && (
        <FilterComponent
          where={window.location.pathname}
          filter={globalFilter}
          setFilter={setGlobalFilter}
          headerGroups={headerGroups}
          rolesData={rolesData}
          facilitiesData={facilitiesData}
          setRoleFilter={setRoleFilter}
          facilityFilter={userFacilityFilter}
          setFacilityFilter={setUserFacilityFilter}
          roleFilter={roleFilter}
          showAddNewUserBtn={showAddNewUserBtn}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          handleClearFilter={handleClearFilter}
          searchable={searchable}
          clearReactSelect={clearReactSelect}
          searchPlaceholder={searchPlaceholder}
        />
      )}
      <div>
        <div>
          <div className="flex flex-row justify-between">
            {showSearchBar && (
              <SearchGridItem>
                <GlobalFilter
                  containerClassName="border border-solid border-gray-300 "
                  // searchBarClassName="lg:w-96"
                  filter={globalFilter}
                  half={half}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  disabled={incidentTypeParam || facilityNameParam}
                  placeholder={searchPlaceholder}
                />
              </SearchGridItem>
            )}
            {showAddIncidentButton && (
              <AddIncidentGridItem className="ml-3">
                <AddIncidentButtonContainer>
                  <AddIncidentButton onClick={doAddIncident}>
                    <AddIcon src={addIcon} alt="plus icon" />
                    New Incident
                  </AddIncidentButton>
                </AddIncidentButtonContainer>
              </AddIncidentGridItem>
            )}
          </div>

          {showSelectFacilitiesComponent && (
            <FilterComponentGeneral
              incidentTypeData={incidentType}
              incidentTypeFilter={incidentTypeFilter}
              setIncidentTypeFilter={(type) => setIncidentTypeFilter(type)}
              incidentStatusData={incidentStatus}
              incidentStatusFilter={incidentStatusFilter}
              setIncidentStatusFilter={(status) =>
                setIncidentStatusFilter(status)
              }
              incidentFacilityData={facilitiesData}
              genIncidentFacilityFilter={incidentFacilityFilter}
              setGenIncidentFacilityFilter={setIncidentFacilityFilter}
              handleClearFilter={handleClearFilter}
              incidentTypeParam={incidentTypeParam}
              facilityNameParam={facilityNameParam}
              searchable={searchable}
              clearReactSelect={clearReactSelect}
              draft={draft}
              draftIncidentFacilityFilter={draftFacilityFilter}
              setDraftIncidentFacilityFilter={setDraftFacilityFilter}
              draftIncidentTypeFilter={draftIncidentTypeFilter}
              where={whereUsed}
              setDraftIncidentTypeFilter={setDraftIncidentTypeFilter}
              startDate={getStartDate()}
              endDate={getEndDate()}
              setStartDate={getSetStartDate()}
              setEndDate={getSetEndDate()}
              deletedFacilityFilter={deletedIncidentFacilityFilter}
              setDeletedFacilityFilter={setDeletedIncidentFacilityFilter}
              deletedTypeFilter={deletedIncidentTypeFilter}
              setDeletedTypeFilter={setDeletedIncidentTypeFilter}
              deletedStatusFilter={deletedIncidentStatusFilter}
              setDeletedStatusFilter={setDeletedIncidentStatusFilter}
            />
          )}
        </div>
      </div>
      <div id="parent-container" ref={tableContainer}>
        {showTitleBar && <TitleBar>{barTitle}</TitleBar>}
        
        {showExportBtn && (
          <div className="export-row flex gap-[10px] mb-[15px] mt-[15px]">
            <button
              className="k-button k-button-md k-button-solid k-rounded-md bg-[var(--main-purple-light)] text-white border-none px-4 py-2 rounded cursor-pointer"
              onClick={handleExcelExport}
            >
              Export Excel
            </button>
            <button
              className="k-button k-button-md k-button-solid k-rounded-md bg-[var(--main-purple-light)] text-white border-none px-4 py-2 rounded cursor-pointer"
              onClick={handlePDFExport}
            >
              Export PDF
            </button>
          </div>
        )}

        <div className="border-red mt-2 px-2 md:px-0">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-x-hidden border-b border-gray-200 shadow">
                <table
                  {...getTableProps()}
                  className="table-fixe min-w-full border-collapse border"
                >
                  {caption ? <Caption>{caption}</Caption> : null}
                  <thead className="table_header_bcg text-left">
                    {headerGroups.map((headerGroup, i) => (
                      <tr
                        {...headerGroup.getHeaderGroupProps()}
                        className="fix_header"
                      >
                        {headerGroup.headers.map((column) => (
                          <td
                            // @ts-ignore
                            key={column.id}
                            style={{
                              minWidth: "260px",
                              border: "1px solid red",
                            }}
                            className="whitespace-nowrap py-5 pl-1 text-center uppercase"
                            {...column.getHeaderProps(
                              // @ts-ignore
                              column.getSortByToggleProps()
                            )}
                          >
                            <div
                              className={`table_data_color flex w-full items-center text-left ${
                                textIsLong(column.render("Header")) && "text-xs"
                              }`}
                            >
                              {column.render("Header")}
                              {/* Add a sort direction indicator */}
                              <span>{handleSortIconDisplay(column)}</span>
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  {!isLoading && !isFetching && (
                    <tbody {...getTableBodyProps()} className="bg-white">
                      {filteredData.map((row: any, rowIndex: number) => {
                        prepareRow(row);

                        return (
                          <Tr
                            style={{ color: "gray" }}
                            {...row.getRowProps()}
                            styledRow={styledRow}
                          >
                            {row.cells.map((cell: any, cellIndex: any) => {
                              const isFirstRow =
                                rowIndex === 0 ||
                                filteredData[rowIndex - 1]?.original
                                  .ResidentName !== row.original.ResidentName;

                              const previousRow =
                                filteredData[rowIndex - 1]?.original;

                              let isFirstRowExist = false;
                              if (previousRow) {
                                isFirstRowExist = "isFirstRow" in previousRow;
                              }

                              return (
                                <td
                                  data-testid={`table-cell-${row.id}-${cell.column.id}`}
                                  {...cell.getCellProps()}
                                  style={{
                                    color:
                                      cell.row.original.Status !== "Active" &&
                                      onUserList
                                        ? "var(--pagina_text_color)"
                                        : "table_data_color",
                                    border: demarkated
                                      ? "1px solid #8b6697"
                                      : undefined,
                                  }}
                                  className="table_data_color container mx-auto flex-1 py-3 pl-1 text-left"
                                >
                                  {cell.column.id === "ResidentName" &&
                                  isFirstRowExist &&
                                  !isFirstRow
                                    ? ""
                                    : cell.render("Cell", {
                                        cb: (item: any) => {
                                          getUserCb(item);
                                        },
                                      })}

                                  {hasTag &&
                                    cellIndex === tagPosition &&
                                    row.original.FacilityCount > 1 && (
                                      <div
                                        className="ml-3 inline-block"
                                        style={{ position: "relative" }}
                                      >
                                        <ButtonSpan
                                          onClick={() => {
                                            getUserCb(row.original);
                                          }}
                                          data-tooltip-id="my-tooltip"
                                          data-tooltip-html={`<div>${renderToolTipContent(
                                            row.original.facilityList || []
                                            //@ts-ignore
                                          )}</div>`}
                                        >
                                          <Tag>
                                            {" "}
                                            <span className="text-xs">
                                              +{row.original.FacilityCount - 1}
                                            </span>{" "}
                                          </Tag>
                                        </ButtonSpan>
                                      </div>
                                    )}
                                </td>
                              );
                            })}
                          </Tr>
                        );
                      })}
                    </tbody>
                  )}
                </table>
                <Tooltip id="my-tooltip" />
                {filteredData.length === 0 && !isLoading && (
                  <div className="mt-10 text-center text-xl text-gray-300">
                    No Record available
                  </div>
                )}
                {isLoading && (
                  <div className="mt-10 text-center text-xl text-gray-300">
                    <CustomLoader table />
                  </div>
                )}
                {isFetching && !isLoading && (
                  <div className="mt-10 text-center text-xl text-gray-300">
                    <CustomLoader table />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {!noPagination && (
        <StyledPagination
          previousPage={previousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          gotoPage={gotoPage}
          pageCount={pageCount}
          pageSize={pageSize}
          setPageSize={setPageSize}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
          currentPage={currentPage}
          serverPaginated={serverPaginated}
          noOfPages={noOfPages}
          goToFirstPage={goToFirstPage}
          goToLastPage={goToLastPage}
          handlePageSize={handlePageSize}
        />
      )}
    </>
  );
};

export default StyledTable;
